import React from 'react';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import { getStatColor, getStatTooltip } from '@/utils/unitStatHelpers';
import './ShopStatDisplay.css';

/**
 * Mapping from stat key to display info
 * Using full names for better readability in Shop
 */
const STAT_CONFIG = {
  strength: { key: 'STR', label: 'Strength', fullName: 'Strength', description: 'Physical damage dealt in combat' },
  armor: { key: 'ARM', label: 'Armor', fullName: 'Armor', description: 'Physical damage reduction' },
  psi: { key: 'PSI', label: 'Psi', fullName: 'Psi', description: 'Psychic power and abilities' },
  will: { key: 'WILL', label: 'Will', fullName: 'Will', description: 'Mental resistance' },
  max_hp: { key: 'HP', label: 'Health', fullName: 'Health Points', description: 'Health Points - unit dies at 0' },
  max_ap: { key: 'AP', label: 'AP', fullName: 'Action Points', description: 'Action Points per turn' },
  agility: { key: 'SPD', label: 'Speed', fullName: 'Speed', description: 'Turn order priority' },
  max_movement: { key: 'MOV', label: 'Move', fullName: 'Movement', description: 'Movement range per turn' },
  vision: { key: 'VIS', label: 'Vision', fullName: 'Vision', description: 'Sight range for detecting enemies' },
};

/**
 * ShopStatDisplay - Compact stat display with color coding for Shop/Lobby
 *
 * Shows icon + value in a small box, colored by high/avg/low rating.
 * Click/hover shows popup with full stat details.
 */
export default function ShopStatDisplay({
  statName,
  value,
  icon,
  className = '',
}) {
  const config = STAT_CONFIG[statName];
  if (!config) {
    console.warn(`[ShopStatDisplay] Unknown stat: ${statName}`);
    return null;
  }

  const colorClass = getStatColor(config.key, value);

  // Trigger element - compact stat box with label
  const triggerElement = (
    <div className={`shop-stat-display ${colorClass} ${className}`}>
      <span className="shop-stat-icon">{icon}</span>
      <span className="shop-stat-label">{config.label}</span>
      <span className="shop-stat-value">{value}</span>
    </div>
  );

  // Popup content - using standard GameView classes
  const popupContent = (
    <>
      <div className="click-popup-header">
        <div className="click-popup-title-group">
          <span className="click-popup-icon">{icon}</span>
          <span className="click-popup-title">{config.fullName}</span>
        </div>
      </div>
      <div className="click-popup-description">
        {config.description}
      </div>
      <div className="click-popup-separator" />
      <div className="click-popup-details">
        <div className="click-popup-detail-row">
          <span className="click-popup-detail-label">Value</span>
          <span className="click-popup-detail-value">{getStatTooltip(config.key, value)}</span>
        </div>
      </div>
    </>
  );

  return (
    <HoverClickPopup trigger={triggerElement}>
      {popupContent}
    </HoverClickPopup>
  );
}
